import type { CampaignUpdateMutationResponse } from '~/api/graphql/mutations/campaignUpdate.generated';
import type { Campaign } from '~/modules/campaign/types';

import { useCampaignUpdateMutation } from '~/api/graphql/mutations/campaignUpdate.generated';
import { useCampaignDeleteMutation } from '~/api/graphql/mutations/campaignDelete.generated';
import { useTrackingAnalytic } from '~/modules/tracking/hooks/useTrackingAnalytic';
import { CAMPAIGN_MSG, useCampaignStore } from '~/modules/campaign';
import { EVENT_ANALYTIC } from '~/modules/tracking';
import { useToast } from '~/hooks/useToast';

export interface IDeleteCampaign<T extends Campaign> {
  isLoadingDelete: boolean;
  onDeleteCampaign: (campaign: T) => Promise<void>;
}

export const useDeleteCampaign = <T extends Campaign>(): IDeleteCampaign<T> => {
  const { shopifyToast } = useToast();
  const { trackEvent } = useTrackingAnalytic();

  const removeCampaign = useCampaignStore((state) => state.removeCampaign);
  const deleteCampaignMutation = useCampaignDeleteMutation();

  const onDeleteCampaign = async (campaign: T): Promise<void> => {
    if (!campaign.id) return;

    const response = await deleteCampaignMutation.mutateAsync({
      id: campaign.id,
    });

    if (!response) {
      shopifyToast(CAMPAIGN_MSG.DELETE.FAILED, { isError: true });
      return;
    }

    // const isSyncLivePage = campaignAllowedSyncWithLivePages(campaign);
    // if (isSyncLivePage) await handleSyncToLivePage();

    removeCampaign(campaign.id);
    shopifyToast(CAMPAIGN_MSG.DELETE.SUCCESS);
    trackEvent(EVENT_ANALYTIC.GEMX_CAMPAIGN_DELETE, {
      campaign: {
        ...campaign,
      },
    });
  };

  return {
    isLoadingDelete: deleteCampaignMutation.isLoading,
    onDeleteCampaign,
  };
};

export const useUpdateDeleteCampaignMutation = () => {
  const campaignCampaignMutation = useCampaignUpdateMutation();

  const mutateAsync = async ({
    id,
  }: {
    id: string;
  }): Promise<CampaignUpdateMutationResponse | undefined> => {
    if (!id) return;

    const response = await campaignCampaignMutation.mutateAsync({
      id: id,
      input: { deletedAt: new Date().toISOString() },
    });

    return response;
  };

  return {
    isLoading: campaignCampaignMutation.isLoading,
    mutateAsync,
  };
};
